import { Badge, Button, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faBoltLightning, faClockRotateLeft, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { MAX_CARRIED_CONSUMERS } from 'api/events/endpoints/getLiveEvents';
import { Columns } from 'components/base/Columns';
import StatusBadge from 'components/base/StatusBadge';
import { DetailCard } from 'components/details/DetailCard';
import FormatDateTime from 'components/common/FormatDateTime';

import { NodeInstanceLink } from 'pages/apps/platform/list_node_instances/NodeInstanceLink';

import { EntityLink } from './EntityLink';
import { nodeDefinitionUrl } from './entityUrls';
import type { LiveEventCardModel } from './LiveEventCardModel';
import './LiveEventCard.scss';

interface LiveEventCardProps {
  readonly card: LiveEventCardModel;
  readonly onShowSurplus: () => void;
  readonly onShowSources: () => void;
  /** Focus is owned by the URL, so pinning is the page's to perform. */
  readonly onTogglePin: () => void;
}

/**
 * One event's card: supply on the left, demand on the right — the rendezvous
 * read left to right, with auto-submit as a third column when the page is
 * counting it.
 *
 * A pure observer over {@link LiveEventCardModel}. Collapse is controlled by
 * the model rather than by the card's own state, so the page's expand-all /
 * collapse-all reaches cards that have never been rendered.
 */
export const LiveEventCard = observer(({ card, onShowSurplus, onShowSources, onTogglePin }: LiveEventCardProps) => {
  const title = (
    <span className="live-event-card__title">
      {/* By id: `search` is a query param of the events route but not one of
          its declared filters, so the list would silently ignore it. */}
      <Link to={`/automation/events?event_id=${card.eventId}`}>{card.name}</Link>
      <span className="text-muted live-event-card__zone">{card.zoneName}</span>
      {card.forceVisible && (
        // Says why an event with nothing outstanding is on the page at all.
        <Badge bg="secondary" className="live-event-card__badge">
          {card.focused ? 'Focused' : 'Kept'}
        </Badge>
      )}
    </span>
  );

  const actions = (
    <>
      <Button
        variant="link"
        className={card.focused ? 'live-event-card__icon-button live-event-card__icon-button--active' : 'live-event-card__icon-button'}
        title={card.focused ? 'Unpin this event' : 'Pin this event to the top'}
        aria-label={card.focused ? 'Unpin this event' : 'Pin this event to the top'}
        aria-pressed={card.focused}
        onClick={onTogglePin}
      >
        <FontAwesomeIcon icon={faThumbtack} />
      </Button>
      <Button
        variant="link"
        className="live-event-card__icon-button"
        title="Refresh this card"
        aria-label="Refresh this card"
        onClick={card.refresh}
        disabled={card.busy}
      >
        {card.busy ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faArrowsRotate} />}
      </Button>
    </>
  );

  return (
    <DetailCard
      title={title}
      subtitle={`${card.surplusCount} outstanding · ${card.waitingCount} waiting`}
      collapsible
      collapsed={card.collapsed}
      onToggleCollapsed={card.toggleCollapsed}
      actions={actions}
    >
      {card.error !== null && (
        <div className="live-event-card__error text-danger" role="alert">
          {card.error}
        </div>
      )}

      <Columns>
        <Columns.Column heading="Supply" subtitle="Occurrences waiting to be consumed.">
          <div className="live-event-card__supply">
            <Button variant="link" className="live-event-card__link" onClick={onShowSurplus}>
              {card.surplusCount === 1 ? '1 outstanding occurrence' : `${card.surplusCount} outstanding occurrences`}
            </Button>
            <Button variant="link" className="live-event-card__link" onClick={onShowSources}>
              What can raise this
            </Button>
            {/* The other side of the consumption boundary: this card is what is
                still outstanding, that page is what no longer is. */}
            {/* An anchor, not a Button: it navigates. Bootstrap's .btn/.btn-link
                classes give it the same look as the two buttons above it. */}
            <Link to={`/automation/event_history?event_id=${card.eventId}`} className="live-event-card__link btn btn-link">
              <FontAwesomeIcon icon={faClockRotateLeft} className="me-1" />
              Show history
            </Link>
            <Button variant="outline-primary" size="sm" className="live-event-card__raise" onClick={card.raise} disabled={card.busy}>
              <FontAwesomeIcon icon={faBoltLightning} className="me-1" />
              Raise
            </Button>
          </div>
        </Columns.Column>

        <Columns.Column heading="Demand" subtitle="Node instances parked on this event.">
          {card.consumers.length === 0 ? (
            <p className="text-muted mb-0">Nothing is waiting.</p>
          ) : (
            <Table size="sm" className="live-event-card__table mb-0">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Status</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {card.consumers.map((consumer) => (
                  // A consumer this event has already served stays listed, so a
                  // raise visibly moves it on rather than dropping it.
                  <tr key={consumer.node_instance_id} className={consumer.is_waiting ? undefined : 'live-event-card__row--served'}>
                    <td className="live-event-card__name-cell">
                      <NodeInstanceLink nodeInstanceId={consumer.node_instance_id} nodeName={consumer.node_name} rootNodeInstanceId={null} />
                    </td>
                    <td>{consumer.status === null ? '—' : <StatusBadge status={consumer.status} />}</td>
                    <td>{consumer.start_datetime === null ? '—' : <FormatDateTime value={consumer.start_datetime} />}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {/* The count above stays true when the list is cut, so say which one
              is short rather than letting the table read as the whole demand. */}
          {card.consumersTruncated && <p className="text-muted small mb-0 mt-2">Showing the first {MAX_CARRIED_CONSUMERS}; this event has more waiting.</p>}
        </Columns.Column>

        {card.subscriptions.length > 0 && (
          <Columns.Column heading="Auto-submit" subtitle="Definitions a raise would submit.">
            <Table size="sm" className="live-event-card__table mb-0">
              <thead>
                <tr>
                  <th>Definition</th>
                  <th>Mode</th>
                  <th>Timezone</th>
                </tr>
              </thead>
              <tbody>
                {card.subscriptions.map((subscription) => (
                  <tr key={subscription.node_definition_id}>
                    <td className="live-event-card__name-cell">
                      <EntityLink id={subscription.node_definition_id} to={nodeDefinitionUrl}>
                        {subscription.node_name ?? `Definition ${subscription.node_definition_id}`}
                      </EntityLink>
                    </td>
                    <td className="text-muted">{subscription.auto_submit_mode ?? '—'}</td>
                    {/* No override means the configured default timezone, not an unknown one. */}
                    <td className="text-muted">{subscription.auto_submit_timezone ?? 'default'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Columns.Column>
        )}
      </Columns>
    </DetailCard>
  );
});
